import {
	blankTabUrls,
	browsingToolsUrls,
	cameraAppUrls,
	chromeSettingsUrls,
	developerToolsUrls,
	dinoGameUrls,
	edgeNewTabUrls,
	edgeSettingsUrls,
	fileUrls,
	googleNewTabUrls,
	mediaAppUrls,
	screencastAppUrls,
} from "@/js/utils/screens/UrlCategories";
import { surfGameUrls } from "@/js/utils/screens/UrlCategories";
import { ClassroomStudentActivityState } from "@/js/components/RegularClassroomUser/utils";

// --- Icons ---
import browsingToolsIcon from "@/assets/studentTile/browsing-tools-icon.svg?url";
import newTabIcon from "@/assets/studentTile/new-tab-icon.svg?url";
import diagnosticIcon from "@/assets/studentTile/diagnostic-icon.svg?url";
import privateTabIcon from "@/assets/studentTile/private-tab-icon.svg?url";
import settingsTileIcon from "@/assets/studentTile/settings-tile-icon.svg?url";
// ---

const urlMatchesCategory = (url: string, categoryUrls: string[]) => {
	return categoryUrls.some((u: string) => url.startsWith(u));
};

export type ScreenContextCopyKey =
	| "studentTile.screenContext.dinoGame"
	| "studentTile.screenContext.surfGame"
	| "studentTile.screenContext.newGoogleTab"
	| "studentTile.screenContext.newEdgeTab"
	| "studentTile.screenContext.blankTab"
	| "studentTile.screenContext.cameraApp"
	| "studentTile.screenContext.mediaApps"
	| "studentTile.screenContext.viewingFile"
	| "studentTile.screenContext.browsingTools"
	| "studentTile.screenContext.chromeSettings"
	| "studentTile.screenContext.edgeSettings"
	| "studentTile.screenContext.developerTools"
	| "studentTile.screenContext.browserExtension"
	| "studentTile.screenContext.screencastApp"
	| "studentTile.screenContext.screenshotCaptureIssue"
	| "studentTile.screenContext.noBrowserOpen"
	| "studentTile.screenContext.screenshotCaptureNotAllowed"
	| "studentTile.screenContext.screenCaptureTimeout";

export const getCopy = (url: string, state: ClassroomStudentActivityState | undefined): ScreenContextCopyKey | undefined => {
	switch (true) {
		case state === ClassroomStudentActivityState.SCREEN_CAPTURE_TIMEOUT:
			return "studentTile.screenContext.screenCaptureTimeout";
		case urlMatchesCategory(url, dinoGameUrls):
			return "studentTile.screenContext.dinoGame";
		case urlMatchesCategory(url, surfGameUrls):
			return "studentTile.screenContext.surfGame";
		case urlMatchesCategory(url, googleNewTabUrls):
			return "studentTile.screenContext.newGoogleTab";
		case urlMatchesCategory(url, edgeNewTabUrls):
			return "studentTile.screenContext.newEdgeTab";
		case urlMatchesCategory(url, blankTabUrls):
			return "studentTile.screenContext.blankTab";
		case urlMatchesCategory(url, cameraAppUrls):
			return "studentTile.screenContext.cameraApp";
		case urlMatchesCategory(url, mediaAppUrls):
			return "studentTile.screenContext.mediaApps";
		case urlMatchesCategory(url, fileUrls):
			return "studentTile.screenContext.viewingFile";
		case urlMatchesCategory(url, browsingToolsUrls):
			return "studentTile.screenContext.browsingTools";
		case urlMatchesCategory(url, chromeSettingsUrls):
			return "studentTile.screenContext.chromeSettings";
		case urlMatchesCategory(url, edgeSettingsUrls):
			return "studentTile.screenContext.edgeSettings";
		case urlMatchesCategory(url, developerToolsUrls):
			return "studentTile.screenContext.developerTools";
		case url.startsWith("chrome-extension://"):
			return "studentTile.screenContext.browserExtension";
		case urlMatchesCategory(url, screencastAppUrls):
			return "studentTile.screenContext.screencastApp";
		case state === ClassroomStudentActivityState.SCREEN_CAPTURE_FAILED:
			return "studentTile.screenContext.screenshotCaptureIssue";
		case state === ClassroomStudentActivityState.NO_LAST_FOCUSED_WINDOW:
			return "studentTile.screenContext.noBrowserOpen";
		case state === ClassroomStudentActivityState.SCREEN_CAPTURE_DISALLOWED:
			return "studentTile.screenContext.screenshotCaptureNotAllowed";
		default:
			// Fallback for any uncategorised chrome:// pages
			if (url.startsWith("chrome://")) {
				return "studentTile.screenContext.chromeSettings";
			}
			// Fallback for any uncategorised edge:// pages
			if (url.startsWith("edge://")) {
				return "studentTile.screenContext.edgeSettings";
			}
			return undefined;
	}
};

export const getImprovedTitle = (title: string): string => {
	switch (true) {
		case title === "calculator.apps.chrome":
			return "Calculator app";
		default:
			return title;
	}
};

export const getIcon = (url: string, state: ClassroomStudentActivityState | undefined): string => {
	switch (true) {
		case urlMatchesCategory(url, googleNewTabUrls):
		case urlMatchesCategory(url, edgeNewTabUrls):
		case urlMatchesCategory(url, blankTabUrls):
			return newTabIcon;
		case urlMatchesCategory(url, browsingToolsUrls):
			return browsingToolsIcon;
		case urlMatchesCategory(url, chromeSettingsUrls):
		case urlMatchesCategory(url, edgeSettingsUrls):
			return settingsTileIcon;
		case urlMatchesCategory(url, developerToolsUrls):
			return diagnosticIcon;
		case urlMatchesCategory(url, dinoGameUrls):
		case urlMatchesCategory(url, cameraAppUrls):
		case urlMatchesCategory(url, mediaAppUrls):
		case urlMatchesCategory(url, fileUrls):
		case urlMatchesCategory(url, surfGameUrls):
		case state === ClassroomStudentActivityState.SCREEN_CAPTURE_FAILED:
		case state === ClassroomStudentActivityState.NO_LAST_FOCUSED_WINDOW:
		case state === ClassroomStudentActivityState.SCREEN_CAPTURE_DISALLOWED:
		case state === ClassroomStudentActivityState.SCREEN_CAPTURE_TIMEOUT:
		default:
			// Fallback for any uncategorised chrome:// pages
			if (url.startsWith("chrome://")) {
				return settingsTileIcon;
			}
			return privateTabIcon;
	}
};
